import * as React from 'react';
import _ from 'lodash';

import { MaskedTextField, PrimaryButton, Spinner, SpinnerSize, TextField, Toggle, ChoiceGroup, DatePicker, Stack } from '@fluentui/react';
import { GENDER_TYPE, PATIENT_LANGUAGES } from '../../../config/constants';

import { Account } from '../../../api/accounts.types';
import { formatDate } from '../../../util/Date';

export interface AccountFormProps {
  actions: any;
  isUpdating: boolean;
  account?: Account;
}

interface AccountFormState {
  email?: string;
  error?: string;
  firstName: string;
  id?: string;
  lastName: string;
  phoneNumber?: string;
  userAccountId: string;
  username: string;
  zipcode: string;
  noEmail: boolean;
  dateOfBirth?: Date;
  preferredLanguage: string;
  gender?: string;
}

export class AccountForm extends React.Component<AccountFormProps, AccountFormState> {
  constructor(props: AccountFormProps) {
    super(props);

    this.state = {
      email: '',
      firstName: '',
      id: '',
      lastName: '',
      phoneNumber: '',
      userAccountId: '',
      username: '',
      zipcode: '',
      preferredLanguage: 'en',
      noEmail: false,
    };
  }

  public componentDidMount() {
    const currentAccount = this.props.account;
    if (currentAccount) {
      this.setAccountData(currentAccount);
    }
  }

  public componentDidUpdate(prevProps: AccountFormProps) {
    if (
      prevProps.account !== undefined &&
      this.props.account !== undefined &&
      prevProps.account &&
      this.props.account &&
      prevProps.account.userAccountId !== this.props.account.userAccountId
    ) {
      this.props.actions.clearError();
    }

    if (this.props.account !== undefined && prevProps.account !== this.props.account) {
      const currentAccount = this.props.account;
      if (currentAccount) {
        this.setAccountData(currentAccount);
      }
    }
  }

  public render(): JSX.Element {
    if (this.props.isUpdating) {
      return <Spinner size={SpinnerSize.large} style={{ padding: 20 }} label="Saving account information..." ariaLive="assertive" />;
    }

    return (
      <div className="container">
        {this.renderFullForm()}
        <PrimaryButton
          style={{ marginTop: 20 }}
          onClick={this.onSubmit}
          disabled={!this.canSubmit()}
          text={this.props.account!.userAccountId ? 'Save' : 'Add'}
          data-tid="btn_add"
        />
      </div>
    );
  }

  private renderFullForm(): JSX.Element {
    return (
      <Stack tokens={{ childrenGap: 6 }}>
        <TextField name="firstName" value={this.state.firstName} onChange={this.onFirstNameChange} label="First Name" required={true} data-tid="inp_firstName" />

        <TextField name="lastName" value={this.state.lastName} onChange={this.onLastNameChange} label="Last Name" required={true} data-tid="inp_lastName" />

        <div className="flex flex-row">
          <div className="flex-grow">
            <TextField
              name="email"
              value={this.state.email}
              onChange={(ev, value) => this.setState({ email: _.trim(value) })}
              label="E-mail"
              required={true}
              disabled={this.state.noEmail || (this.props.account && !!this.state.id)}
              data-tid="inp_email"
            />
          </div>
          {(!this.props.account || !this.state.id) && (
            <div style={{ marginLeft: 10 }}>
              <Toggle checked={this.state.noEmail} onChange={this.onNoEmailChange} label="No email, auto-generate" data-tid="btn_noEmail" />
            </div>
          )}
        </div>

        {(!this.props.account || !this.state.id) && (
          <ChoiceGroup
            className="inline-flex"
            defaultSelectedKey={this.state.gender}
            options={GENDER_TYPE.map((m) => ({ key: m.text.toUpperCase(), text: m.text }))}
            onChange={this.onGenderRadioChange}
            label="Sex"
            required={true}
            data-tid="btn_gender"
          />
        )}

        {(!this.props.account || !this.state.id) && (
          <ChoiceGroup
            className="inline-flex"
            defaultSelectedKey={this.state.preferredLanguage}
            options={Object.entries(PATIENT_LANGUAGES).map(([key, value]) => ({ key, text: value }))}
            onChange={this.onPreferredLanguageChange}
            label="Preferred Language"
            required
          />
        )}

        {(!this.props.account || !this.state.id) && (
          <DatePicker
            onSelectDate={this.onBirthdayChange}
            label="Date of Birth (mm/dd/yyyy)"
            value={this.state.dateOfBirth}
            isRequired={true}
            allowTextInput={true}
            disableAutoFocus={true}
            formatDate={formatDate}
            data-tid="inp_DOB"
          />
        )}

        <MaskedTextField
          name="zipcode"
          value={this.state.zipcode}
          onChange={(ev, value) => this.setState({ zipcode: _.trim(value) })}
          label="ZIP Code"
          required={true}
          mask="99999"
          maskChar=""
          maxLength={5}
          data-tid="inp_zip"
        />

        <MaskedTextField
          name="phone"
          value={this.state.phoneNumber}
          onChange={(ev, value) => this.setState({ phoneNumber: _.trim(value) })}
          label="Phone Number"
          required={true}
          mask="999-999-9999"
          maskChar=""
          maxLength={14}
          data-tid="inp_phone"
        />
      </Stack>
    );
  }

  private onFirstNameChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
    const v = _.trimStart(value);
    this.setState({
      firstName: _.trimStart(v),
      email: this.state.noEmail ? this.generateEmail(v, this.state.lastName) : this.state.email,
    });
  };

  private onLastNameChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
    const v = _.trimStart(value);
    this.setState({
      lastName: v,
      email: this.state.noEmail ? this.generateEmail(this.state.firstName, v) : this.state.email,
    });
  };

  private onGenderRadioChange = (e?: React.FormEvent, option?: { text: string; key: string }) => {
    this.setState({ gender: option!.key });
  };

  private onPreferredLanguageChange = (e?: React.FormEvent, option?: { text: string; key: string }) => {
    option?.key && this.setState({ preferredLanguage: option.key });
  };

  private onBirthdayChange = (date: any) => {
    this.setState({ dateOfBirth: date instanceof Date ? date : undefined });
  };

  private onNoEmailChange = (ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    this.setState({
      noEmail: checked!,
      email: this.generateEmail(this.state.firstName, this.state.lastName),
    });
  };

  private generateEmail(first: string, last: string): string {
    return `noemail+${first.replace(/ /g, '')}.${last.replace(/ /g, '')}@heal.com`;
  }

  private canSubmit(): boolean {
    return (
      !this.props.isUpdating &&
      !!this.state.firstName &&
      !!this.state.lastName &&
      !!this.state.phoneNumber &&
      !!this.state.zipcode &&
      ((!this.state.userAccountId && !!this.state.dateOfBirth && !!this.state.gender) || !!this.state.userAccountId) &&
      !!this.state.email
    );
  }

  private onSubmit = () => {
    if (this.state.userAccountId) {
      this.props.actions.editUserAccount(this.state.userAccountId, this.state);
    }
  };

  private setAccountData(currentAccount: Account) {
    if (Object.keys(currentAccount).length === 0) {
      this.setState({
        email: '',
        error: undefined,
        firstName: '',
        lastName: '',
        id: '',
        phoneNumber: '',
        username: '',
        userAccountId: '',
        zipcode: '',
        gender: '',
      });
    } else {
      this.setState({
        email: currentAccount.email,
        firstName: currentAccount.firstName,
        id: currentAccount.userAccountId,
        lastName: currentAccount.lastName,
        phoneNumber: currentAccount.phoneNumber.charAt(0) === '+' ? currentAccount.phoneNumber.substr(2) : currentAccount.phoneNumber,
        userAccountId: currentAccount.userAccountId,
        username: currentAccount.email,
        zipcode: currentAccount.zipcode || currentAccount.signupZipcode,
        error: undefined,
        dateOfBirth: currentAccount.dateOfBirth,
        gender: currentAccount.gender,
      });
    }
  }
}
